import React, { useState, useEffect } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import { FaClock } from "react-icons/fa";
import { useGetConfiguracaoEmail } from "hooks/groupPay/useGetConfiguracaoEmail";
import { useGetEmailRedirecionamento } from "hooks/groupPay/useGetEmailRedirecionamento";
import showAlert from "shared/util/functions/alert/showAlert";
import AlertBanner from "shared/components/alertBanner/AlertBanner";
import { EnumTipoConfiguracaoEmail } from "constants/EnumTipoConfiguracaoEmail";
import ConfigurarAcessoDireto from "./ConfigurarAcessoDireto";
import ConfigurarEmailUnico from "./ConfigurarEmailUnico";
import ModalLogAcesso from "./modais/ModalLogAcesso";
import Header from "./components/ConfiguracaoHeader";
import BuscaDesabilitada from "./components/BuscaDesabilitada";
import { useStyles } from "./styles/ConfigurarBuscaNFEmailStyles";
import OpcaoConfiguracao from "./components/OpcaoConfiguracao";
import StandardModalEmailUnico from "shared/components/standardModal/StandardModalEmailUnico";
import StandardModalCaixaEmailPay from "shared/components/standardModal/StandardModalCaixaEmailPay";
import StandardModalTermoAceite from "shared/components/standardModal/StandardModalTermoAceite";
import toast from "react-hot-toast";
import { usePutInativarConfiguarcaoBuscaEmail } from "hooks/groupPay/usePutInativarConfiguarcaoBuscaEmail";
import { NotificationProps } from "shared/components/notification/Notification";

interface ConfigurarBuscaNFEmailProps {
  isInsideWizard?: boolean;
  onConfiguracaoSalva?: (isConfigurado: boolean) => void;
  onToggleBuscaPorEmail?: (ativo: boolean) => void;
}

const ConfigurarBuscaNFEmail: React.FC<ConfigurarBuscaNFEmailProps> = ({
  isInsideWizard = false,
  onConfiguracaoSalva,
  onToggleBuscaPorEmail,
}) => {
  const classes = useStyles();
  const [selectedOption, setSelectedOption] = useState<EnumTipoConfiguracaoEmail>(
    EnumTipoConfiguracaoEmail.REDIRECIONAMENTO_ALIAS
  );
  const [isBuscaPorEmailAtiva, setIsBuscaPorEmailAtiva] = useState(true);
  const [notification, setNotification] = useState<NotificationProps>({ show: false });
  const [showModalLogAcesso, setShowModalLogAcesso] = useState(false);
  const [showModalEmailUnico, setShowModalEmailUnico] = useState(false);
  const [showModalAcessoADM, setShowModalAcessoADM] = useState(false);
  const [showModalTermoAceite, setShowModalTermoAceite] = useState(false);

  const { data: emailRedirecionamentoResponse, isLoading: isLoadingEmail } = useGetEmailRedirecionamento();
  const {
    data: configuracaoData,
    isCarregandoInicial: isCarregandoConfig,
    isError: falhouAoBuscarConfig,
    refetch: recarregarConfiguracao,
  } = useGetConfiguracaoEmail();
  const { mutateAsync: inativarConfiguracaoEmailAsync } = usePutInativarConfiguarcaoBuscaEmail();

  useEffect(() => {
    if (!isInsideWizard) {
      if (configuracaoData?.payload?.excluido === true) {
        setIsBuscaPorEmailAtiva(false);
      } else {
        setIsBuscaPorEmailAtiva(true);
        if (configuracaoData?.payload?.tipoConfiguracaoEmail) {
          setSelectedOption(configuracaoData?.payload?.tipoConfiguracaoEmail);
        }
      }
    }
  }, [configuracaoData, isInsideWizard]);

  const groupPayEmail = emailRedirecionamentoResponse?.payload || "";

  const handleToggleChange = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (!checked && !isInsideWizard) {
      showAlert({
        tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
        texto: "Tem certeza que deseja desativar a busca por e-mail?",
        onConfirm: async () => {
          try {
            await inativarConfiguracaoEmailAsync(groupPayEmail);
            toast.success("Configuração inativada com sucesso!");
            setNotification({ show: false });
            setSelectedOption(EnumTipoConfiguracaoEmail.REDIRECIONAMENTO_ALIAS);
            setIsBuscaPorEmailAtiva(false);
            onToggleBuscaPorEmail?.(false);
          } catch (error) {
            toast.error("Não foi possível inativar a configuração.");
          }
        },
      });
      return;
    }

    if (!checked) {
      setNotification({ show: false });
    }
    setSelectedOption(EnumTipoConfiguracaoEmail.REDIRECIONAMENTO_ALIAS);
    setIsBuscaPorEmailAtiva(checked);
    onToggleBuscaPorEmail?.(checked);
  };

  const onRadioButtonClick = (value: EnumTipoConfiguracaoEmail) => {
    if (value === selectedOption) return;

    showAlert({
      tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
      texto:
        "Você está alterando a forma de busca por e-mail. Os dados preenchidos anteriormente serão apagados. Deseja continuar?",
      onConfirm: () => {
        setNotification({ show: false });
        setSelectedOption(value);
      },
    });
  };

  const getInitialDataForOption = (option: EnumTipoConfiguracaoEmail) => {
    if (configuracaoData?.payload?.tipoConfiguracaoEmail === option && configuracaoData?.payload?.excluido === false) {
      return configuracaoData.payload;
    }
    return null;
  };

  const getCurrentConfigEmail = () => {
    const currentConfig = getInitialDataForOption(selectedOption);
    return currentConfig?.email || "";
  };

  const containerStyle = isInsideWizard ? { marginLeft: "3rem" } : {};

  if (isCarregandoConfig || isLoadingEmail) {
    return (
      <Box
        className={classes.container}
        style={{ ...containerStyle, display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Estado de falha explícito. Antes a tela caía direto no formulário com `configuracaoData`
  // indefinido e os filhos remontavam a cada tentativa, o que realimentava a consulta que
  // acabara de falhar. Aqui o circuito fica aberto até o usuário pedir nova tentativa.
  if (falhouAoBuscarConfig) {
    return (
      <Box className={classes.container} style={{ ...containerStyle, display: "flex", flexDirection: "column", gap: "16px" }}>
        <AlertBanner
          show
          message={
            <>
              Não foi possível carregar a configuração de busca de nota fiscal por e-mail.{" "}
              <strong>Tente novamente</strong>; se o erro persistir, acione o suporte.
            </>
          }
        />
        <Box>
          <Button variant="outlined" onClick={() => recarregarConfiguracao()}>
            Tentar novamente
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box className={classes.container} style={containerStyle}>
        <Header
          isInsideWizard={isInsideWizard}
          checked={isBuscaPorEmailAtiva}
          onChange={handleToggleChange}
          isEmailUnico={selectedOption === EnumTipoConfiguracaoEmail.REDIRECIONAMENTO_ALIAS}
          setShowModalEmailUnico={setShowModalEmailUnico}
          setShowModalAcessoADM={setShowModalAcessoADM}
        />
        {!isBuscaPorEmailAtiva ? (
          <BuscaDesabilitada />
        ) : (
          <>
            <OpcaoConfiguracao
              id="REDIRECIONAMENTO_ALIAS"
              label="E-mail único"
              value={EnumTipoConfiguracaoEmail.REDIRECIONAMENTO_ALIAS}
              checked={selectedOption === EnumTipoConfiguracaoEmail.REDIRECIONAMENTO_ALIAS}
              onChange={() => onRadioButtonClick(EnumTipoConfiguracaoEmail.REDIRECIONAMENTO_ALIAS)}
              notification={notification}
            />
            {selectedOption === EnumTipoConfiguracaoEmail.REDIRECIONAMENTO_ALIAS && (
              <ConfigurarEmailUnico
                key="email-unico"
                groupPayEmail={groupPayEmail}
                handleNotification={setNotification}
                isInsideWizard={isInsideWizard}
                initialData={getInitialDataForOption(EnumTipoConfiguracaoEmail.REDIRECIONAMENTO_ALIAS)}
                onConfiguracaoSalva={onConfiguracaoSalva}
              />
            )}

            <hr className={classes.separator} />

            <OpcaoConfiguracao
              id="ACESSO_DIRETO_CAIXA"
              label="Acesso a caixa de e-mail da administradora"
              value={EnumTipoConfiguracaoEmail.ACESSO_DIRETO_CAIXA}
              checked={selectedOption === EnumTipoConfiguracaoEmail.ACESSO_DIRETO_CAIXA}
              onChange={() => onRadioButtonClick(EnumTipoConfiguracaoEmail.ACESSO_DIRETO_CAIXA)}
              notification={notification}
            >
              {!isInsideWizard && configuracaoData?.payload?.tipoConfiguracaoEmail === EnumTipoConfiguracaoEmail.ACESSO_DIRETO_CAIXA && (
                <span className={classes.accessLogLink} onClick={() => setShowModalLogAcesso(true)}>
                  <FaClock size="12px" /> Ver log de acesso ao e-mail
                </span>
              )}
            </OpcaoConfiguracao>
            {selectedOption === EnumTipoConfiguracaoEmail.ACESSO_DIRETO_CAIXA && (
              <ConfigurarAcessoDireto
                key="acesso-direto"
                handleNotification={setNotification}
                isInsideWizard={isInsideWizard}
                initialData={getInitialDataForOption(EnumTipoConfiguracaoEmail.ACESSO_DIRETO_CAIXA)}
                onConfiguracaoSalva={onConfiguracaoSalva}
                setShowModalTermoAceite={setShowModalTermoAceite}
              />
            )}
          </>
        )}
      </Box>

      <ModalLogAcesso showModal={showModalLogAcesso} onCloseModal={() => setShowModalLogAcesso(false)} email={getCurrentConfigEmail()} />
      <StandardModalEmailUnico showModal={showModalEmailUnico} onCloseModal={() => setShowModalEmailUnico(false)} />
      <StandardModalCaixaEmailPay showModal={showModalAcessoADM} onCloseModal={() => setShowModalAcessoADM(false)} />
      <StandardModalTermoAceite showModal={showModalTermoAceite} onCloseModal={() => setShowModalTermoAceite(false)} />
    </>
  );
};

export default ConfigurarBuscaNFEmail;
